/**
 * @file Portfolio data managed via TanStack Query.
 *
 * The public portfolio page (/portfolio/$profileSlug, no authentication) and
 * the Toolkit editor's draft reads (org-scoped) share these keys, types and
 * hooks.
 */

import type { RouterOutput } from "@repo/api/types";
import {
  queryOptions,
  useMutation,
  useQuery,
  type QueryClient,
} from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { trpcClient } from "../trpc";

// ============================================================================
// Query Keys
// ============================================================================

export const publicProfileKeys = {
  all: ["publicProfile"] as const,
  drafts: () => [...publicProfileKeys.all, "draft"] as const,
  draft: (organizationId: string, profileId: string) =>
    [...publicProfileKeys.drafts(), organizationId, profileId] as const,
  draftHighlightStats: (organizationId: string, profileId: string) =>
    [
      ...publicProfileKeys.all,
      "draftHighlightStats",
      organizationId,
      profileId,
    ] as const,
  portfolios: () => [...publicProfileKeys.all, "portfolio"] as const,
  portfolio: (slug: string) =>
    [...publicProfileKeys.portfolios(), slug] as const,
  socialStats: (slug: string) =>
    [...publicProfileKeys.all, "socialStats", slug] as const,
  highlightStats: (slug: string) =>
    [...publicProfileKeys.all, "highlightStats", slug] as const,
  resolveSlug: (slug: string) =>
    [...publicProfileKeys.all, "resolveSlug", slug] as const,
};

/**
 * Refetch the cached portfolio payloads (getPortfolioDraft /
 * getPublicPortfolio) after an org-side edit to data they carry — name, bio,
 * avatar, location, brand highlights (EPD-12133). Every cached entry is
 * invalidated, as the public ones are keyed by slug; each is a single cheap DB
 * read. Social/highlight stats (legacy-backed and unaffected by profile
 * fields) are deliberately left alone.
 */
export function invalidatePublicProfilePayloads(queryClient: QueryClient) {
  for (const queryKey of [
    publicProfileKeys.drafts(),
    publicProfileKeys.portfolios(),
  ]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

// ============================================================================
// Types — derived from tRPC RouterOutput to prevent type drift
// ============================================================================

/** The portfolio content payload, shared by the editor's draft and the public page. */
export type PublicProfileData = RouterOutput["profile"]["getPortfolioDraft"];
export type PublicProfile = PublicProfileData["profile"];
export type PublicChannel = PublicProfileData["channels"][number];
export type PublicPreferenceItem =
  PublicProfileData["preferences"][string][number];
export type PublicBrandHighlight = PublicProfileData["brandHighlights"][number];

export type PublicPortfolioData = RouterOutput["profile"]["getPublicPortfolio"];

// ============================================================================
// Query Options
// ============================================================================

/**
 * The Toolkit editor's (and its preview's) portfolio content for one of the
 * org's profiles, published or not. Org-scoped server-side.
 */
export function portfolioDraftQueryOptions(
  organizationId: string,
  profileId: string,
) {
  return queryOptions({
    queryKey: publicProfileKeys.draft(organizationId, profileId),
    queryFn: () =>
      trpcClient.profile.getPortfolioDraft.query({ organizationId, profileId }),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry(failureCount, error) {
      const status =
        error instanceof TRPCClientError ? error.data?.httpStatus : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Resolve a public portfolio slug to the profile's current slug (EPD-11640),
 * gated like the page (a published portfolio). Used by the public route to
 * redirect links that use a retired slug.
 */
export function publicSlugResolveQueryOptions(slug: string) {
  return queryOptions({
    queryKey: publicProfileKeys.resolveSlug(slug),
    queryFn: () => trpcClient.profile.resolvePublicSlug.query({ slug }),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
  });
}

// ============================================================================
// Public portfolio (EPD-10101 / EPD-10102) — draft/live gated
// ============================================================================

export function publicPortfolioQueryOptions(slug: string) {
  return queryOptions({
    queryKey: publicProfileKeys.portfolio(slug),
    queryFn: () => trpcClient.profile.getPublicPortfolio.query({ slug }),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry(failureCount, error) {
      const status =
        error instanceof TRPCClientError ? error.data?.httpStatus : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

export function usePublicPortfolioQuery(slug: string) {
  return useQuery(publicPortfolioQueryOptions(slug));
}

// Public per-platform social stats + audience demographics for a live portfolio
// (EPD-10090). Same shape as the editor's channel.getSocialStats.
export type PublicSocialStats = RouterOutput["profile"]["getPublicSocialStats"];

export function usePublicSocialStatsQuery(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: publicProfileKeys.socialStats(slug),
    queryFn: () => trpcClient.profile.getPublicSocialStats.query({ slug }),
    enabled: enabled && Boolean(slug),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // A transient stats failure must not break the already-loaded portfolio;
    // the page just renders without the stats blocks.
    retry(failureCount, error) {
      const status =
        error instanceof TRPCClientError ? error.data?.httpStatus : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

// Read-through post stats + thumbnail for brand-highlight post links
// (EPD-11945), keyed by each link item's exact url. Streamed in after the page
// paints so a slow legacy read never blocks it; until it arrives (or if it
// fails) the tiles show the plain "View post" chip.
export type PublicHighlightPostStats =
  RouterOutput["profile"]["getPublicHighlightStats"];
export type HighlightPostStat = PublicHighlightPostStats[string];

function retryUnless4xx(failureCount: number, error: Error): boolean {
  const status =
    error instanceof TRPCClientError ? error.data?.httpStatus : undefined;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

/** A LIVE portfolio's highlight post stats, for the public page. */
export function usePublicHighlightStatsQuery(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: publicProfileKeys.highlightStats(slug),
    queryFn: () => trpcClient.profile.getPublicHighlightStats.query({ slug }),
    enabled: enabled && Boolean(slug),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: retryUnless4xx,
  });
}

/** One of the org's profiles' highlight post stats, for the editor/preview. */
export function useDraftHighlightStatsQuery(
  organizationId: string,
  profileId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: publicProfileKeys.draftHighlightStats(organizationId, profileId),
    queryFn: () =>
      trpcClient.profile.getHighlightStats.query({ organizationId, profileId }),
    enabled: enabled && Boolean(organizationId) && Boolean(profileId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: retryUnless4xx,
  });
}

/** Fire-and-forget view tracking for a public portfolio (EPD-10090 / EPD-10103). */
export function useTrackPortfolioView() {
  return useMutation({
    mutationFn: (vars: { slug: string }) =>
      trpcClient.profile.trackPortfolioView.mutate(vars),
  });
}

/**
 * A live portfolio's contact email, fetched when a visitor clicks Contact
 * (EPD-12502); the page payload never carries it.
 */
export async function revealPortfolioContactEmail(
  slug: string,
): Promise<string> {
  const { email } = await trpcClient.profile.revealPortfolioContactEmail.mutate(
    { slug },
  );
  return email;
}
